<!--
  ProfileSettings -- the ONE shared personal Profile component.

  Card: "Personal Profile page" -- https://trello.com/c/BxskXBad
  Spec: docs/planning/2026-08-10-users-profile-orders-auth-spec.md,
        "Feature 2 -- Personal Profile page".
  Backend: api/src/endpoints/me_profile.py (the /me family).

  Mounted inside ALL FOUR app chromes -- SuperAdmin, Distributor, Client
  Portal, Storefront -- reached from the profile icon in each app's
  top-right corner, next to Log out. All four apps share one `accounts`
  table, so one component genuinely serves all of them.

  Why plain elements rather than Vuetify
  =====================================
  This component must render identically inside four different chromes,
  and the STOREFRONT chrome is not a Vuetify surface the way the admin
  ones are. Using native inputs + local styles keeps the component
  self-contained: it inherits each app's tokens through CSS custom
  properties but never assumes a component library is installed. That is
  also what lets it be mounted bare in tests, which is the real proof it
  has no chrome dependency.

  Sections
  ========
    1. Your details    -- first/last name + username (live "taken" check)
    2. Email address   -- verification-gated change request
    3. Password        -- current password required, opt-in sign-out
    4. Security        -- two-factor (TOTP) + passkeys

  The Security block is its OWN component (SecuritySettings.vue) rather
  than a fourth section inline: it owns a materially different concern
  (credentials with once-only disclosure rules), it talks to a different
  service, and keeping it separate means this file did not grow by half
  again to accommodate it. Card: https://trello.com/c/lf2Z8aOo

  Role adaptation
  ===============
  `role_slug` comes from the server, not from a locally cached guess.
  Shoppers see the personal basics; other roles additionally see which
  role they hold. Every role gets name / username / email / password --
  those are personal to the human, not to the tenant.

  Live username check
  ===================
  The "that username is taken" warning is UX ONLY and is debounced. The
  REAL guard is the server-side uniqueness check on save: a probe answer
  is stale the instant it returns and two users can both be told
  "available" for the same name. So a failed probe never blocks the save
  button, and a save-time rejection is always rendered.

  ASCII-only.
-->
<template>
    <div class="profile-settings" data-testid="profile-settings-root">
        <p
            v-if="loading"
            class="profile-settings__loading"
            data-testid="profile-loading"
        >
            Loading your profile...
        </p>

        <p
            v-else-if="loadError"
            class="profile-settings__error"
            data-testid="profile-load-error"
        >
            {{ loadError }}
        </p>

        <template v-else>
            <!-- 1. Your details -->
            <section class="profile-card" data-testid="profile-identity-section">
                <h2 class="profile-card__title">Your details</h2>

                <p
                    v-if="showRoleLine"
                    class="profile-card__meta"
                    data-testid="profile-role-line"
                >
                    Role: {{ profile.role_name }}
                </p>

                <label class="profile-field">
                    <span class="profile-field__label">First name</span>
                    <input
                        v-model="firstName"
                        type="text"
                        class="profile-field__input"
                        data-testid="profile-first-name"
                        autocomplete="given-name"
                    />
                </label>

                <label class="profile-field">
                    <span class="profile-field__label">Last name</span>
                    <input
                        v-model="lastName"
                        type="text"
                        class="profile-field__input"
                        data-testid="profile-last-name"
                        autocomplete="family-name"
                    />
                </label>

                <label class="profile-field">
                    <span class="profile-field__label">Username</span>
                    <input
                        v-model="username"
                        type="text"
                        class="profile-field__input"
                        data-testid="profile-username"
                        autocomplete="username"
                        @input="onUsernameInput"
                    />
                </label>

                <p
                    v-if="usernameTaken"
                    class="profile-field__warning"
                    data-testid="profile-username-taken"
                >
                    That username is taken. Please choose another.
                </p>

                <p
                    v-if="identityError"
                    class="profile-settings__error"
                    data-testid="profile-identity-error"
                >
                    {{ identityError }}
                </p>
                <p
                    v-if="identitySaved"
                    class="profile-settings__success"
                    data-testid="profile-identity-success"
                >
                    Your details have been saved.
                </p>

                <button
                    type="button"
                    class="profile-btn"
                    data-testid="profile-save-identity"
                    :disabled="savingIdentity"
                    @click="saveIdentity"
                >
                    {{ savingIdentity ? "Saving..." : "Save details" }}
                </button>
            </section>

            <!-- 2. Email address -->
            <section class="profile-card" data-testid="profile-email-section">
                <h2 class="profile-card__title">Email address</h2>

                <p class="profile-card__meta" data-testid="profile-current-email">
                    Current: {{ profile.email }}
                </p>

                <p class="profile-card__hint">
                    Changing your email needs confirmation. We will send a link
                    to the new address, and your account email will not change
                    until you follow it. We will also let your current address
                    know.
                </p>

                <label class="profile-field">
                    <span class="profile-field__label">New email address</span>
                    <input
                        v-model="newEmail"
                        type="email"
                        class="profile-field__input"
                        data-testid="profile-new-email"
                        autocomplete="email"
                    />
                </label>

                <label class="profile-field">
                    <span class="profile-field__label">Current password</span>
                    <input
                        v-model="emailPassword"
                        type="password"
                        class="profile-field__input"
                        data-testid="profile-email-password"
                        autocomplete="current-password"
                    />
                </label>

                <p
                    v-if="emailError"
                    class="profile-settings__error"
                    data-testid="profile-email-error"
                >
                    {{ emailError }}
                </p>
                <p
                    v-if="emailPending"
                    class="profile-settings__success"
                    data-testid="profile-email-pending"
                >
                    Almost done -- check {{ emailPending }} and confirm the
                    change from that inbox. Your account email stays
                    {{ profile.email }} until you do. If you confirm in a
                    different browser, this page will refresh the moment you
                    come back to it.
                </p>

                <button
                    type="button"
                    class="profile-btn"
                    data-testid="profile-save-email"
                    :disabled="savingEmail"
                    @click="saveEmail"
                >
                    {{ savingEmail ? "Sending..." : "Send confirmation link" }}
                </button>
            </section>

            <!-- 3. Password -->
            <section class="profile-card" data-testid="profile-password-section">
                <h2 class="profile-card__title">Password</h2>

                <label class="profile-field">
                    <span class="profile-field__label">Current password</span>
                    <input
                        v-model="currentPassword"
                        type="password"
                        class="profile-field__input"
                        data-testid="profile-current-password"
                        autocomplete="current-password"
                    />
                </label>

                <label class="profile-field">
                    <span class="profile-field__label">New password</span>
                    <input
                        v-model="newPassword"
                        type="password"
                        class="profile-field__input"
                        data-testid="profile-new-password"
                        autocomplete="new-password"
                    />
                </label>

                <label class="profile-field">
                    <span class="profile-field__label">Confirm new password</span>
                    <input
                        v-model="confirmPassword"
                        type="password"
                        class="profile-field__input"
                        data-testid="profile-confirm-password"
                        autocomplete="new-password"
                    />
                </label>

                <label class="profile-checkbox">
                    <input
                        v-model="signOutOthers"
                        type="checkbox"
                        data-testid="profile-signout-others"
                    />
                    <span>
                        Also sign out my other devices
                    </span>
                </label>

                <p
                    v-if="passwordError"
                    class="profile-settings__error"
                    data-testid="profile-password-error"
                >
                    {{ passwordError }}
                </p>
                <p
                    v-if="passwordSaved"
                    class="profile-settings__success"
                    data-testid="profile-password-success"
                >
                    Your password has been changed. We have emailed you to
                    confirm.
                </p>

                <button
                    type="button"
                    class="profile-btn"
                    data-testid="profile-save-password"
                    :disabled="savingPassword"
                    @click="savePassword"
                >
                    {{ savingPassword ? "Saving..." : "Change password" }}
                </button>
            </section>

            <!-- 4. Security: two-factor + passkeys -->
            <SecuritySettings />
        </template>
    </div>
</template>

<script>
import {
    getMyProfile,
    updateMyProfile,
    checkUsernameAvailable,
    changeMyPassword,
    requestEmailChange,
} from "@/services/profileService";
import SecuritySettings from "./SecuritySettings.vue";
import { validatePassword } from "@/utils/passwordPolicy";

// How long to wait after the last keystroke before asking the server
// whether a username is free. Long enough not to fire per character,
// short enough to feel live.
const USERNAME_PROBE_DEBOUNCE_MS = 350;

// Roles that see only the personal basics. Everyone gets name / username
// / email / password; staff roles additionally see which role they hold,
// which is meaningless to a shopper (there is only one shopper role and
// naming it tells them nothing).
const BASICS_ONLY_ROLES = new Set(["shopper"]);

export default {
    name: "ProfileSettings",
    components: { SecuritySettings },
    data() {
        return {
            loading: true,
            loadError: "",
            profile: {},

            firstName: "",
            lastName: "",
            username: "",
            usernameTaken: false,
            identityError: "",
            identitySaved: false,
            savingIdentity: false,

            newEmail: "",
            emailPassword: "",
            emailError: "",
            emailPending: "",
            savingEmail: false,

            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
            signOutOthers: false,
            passwordError: "",
            passwordSaved: false,
            savingPassword: false,

            _usernameTimer: null,
        };
    },
    computed: {
        showRoleLine() {
            return (
                !!this.profile.role_name &&
                !BASICS_ONLY_ROLES.has(this.profile.role_slug)
            );
        },
    },
    async mounted() {
        await this.load();

        // The email-change confirm link is followed from the NEW mailbox,
        // which in the common case is a DIFFERENT browser -- so this tab
        // never learns the change landed and keeps showing the OLD
        // address indefinitely. A user who did everything right then
        // reads a COMPLETED change as a failed one and tries again.
        //
        // Re-reading on focus is the cheapest correct fix: the server is
        // the only thing that knows the current email, and the moment the
        // user looks at this tab again is exactly when a stale answer
        // starts being wrong. It costs one request per tab-return, only
        // while the page is actually open.
        this._onWindowFocus = () => {
            this.refreshServerState();
        };
        window.addEventListener("focus", this._onWindowFocus);
    },
    beforeUnmount() {
        if (this._usernameTimer) {
            clearTimeout(this._usernameTimer);
        }
        if (this._onWindowFocus) {
            window.removeEventListener("focus", this._onWindowFocus);
            this._onWindowFocus = null;
        }
    },
    methods: {
        /**
         * Pull an error message out of an axios failure.
         *
         * The backend answers with `{error: "..."}`; that message is
         * written for the user (e.g. "Current password is incorrect."),
         * so surface it verbatim rather than replacing it with a generic
         * string that tells the user nothing about what to fix.
         */
        errorMessage(err, fallback) {
            return err?.response?.data?.error || fallback;
        },

        async load() {
            this.loading = true;
            this.loadError = "";
            try {
                const data = await getMyProfile();
                this.profile = data.profile || {};
                this.firstName = this.profile.first_name || "";
                this.lastName = this.profile.last_name || "";
                this.username = this.profile.username || "";
            } catch (err) {
                this.loadError = this.errorMessage(
                    err,
                    "We could not load your profile. Please reload the page."
                );
            } finally {
                this.loading = false;
            }
        },

        /**
         * Re-read the server's answer WITHOUT touching the edit fields.
         *
         * Deliberately not `load()`: that resets firstName / lastName /
         * username from the response and flips `loading`, which would
         * throw away whatever the user had typed and blank the form
         * mid-edit. A background refresh must be invisible unless it
         * actually changes something the user is only READING -- which
         * here is `profile.email`, the value the cross-browser confirm
         * moves.
         *
         * Failures are swallowed on purpose: this fires on every tab
         * return, and a transient network blip must not paint an error
         * over a page the user did not ask to reload.
         */
        async refreshServerState() {
            try {
                const data = await getMyProfile();
                if (data?.profile) {
                    this.profile = data.profile;
                    // The pending banner names an address the user is
                    // still waiting on. Once the server reports that
                    // address AS the account email, the wait is over and
                    // leaving the banner up would contradict the page.
                    if (
                        this.emailPending &&
                        this.profile.email &&
                        this.profile.email.toLowerCase() ===
                            this.emailPending.toLowerCase()
                    ) {
                        this.emailPending = "";
                        this.newEmail = "";
                        this.emailPassword = "";
                    }
                }
            } catch (err) {
                // Intentionally silent -- see the docstring above.
            }
        },

        onUsernameInput() {
            // Any edit invalidates the previous verdict immediately --
            // leaving a stale "taken" warning up while the user types a
            // different name would be worse than showing nothing.
            this.usernameTaken = false;
            if (this._usernameTimer) {
                clearTimeout(this._usernameTimer);
            }
            this._usernameTimer = setTimeout(
                () => this.probeUsername(),
                USERNAME_PROBE_DEBOUNCE_MS
            );
        },

        /**
         * Ask the server whether the typed username is free.
         *
         * UX only. A failure here is swallowed on purpose: the save path
         * enforces uniqueness server-side, so a probe outage must never
         * be able to block a legitimate save.
         */
        async probeUsername() {
            const candidate = (this.username || "").trim();
            if (!candidate || candidate === this.profile.username) {
                this.usernameTaken = false;
                return;
            }
            try {
                const data = await checkUsernameAvailable(candidate);
                // Guard against an out-of-order response overwriting the
                // verdict for a name the user has since edited away from.
                if (candidate !== (this.username || "").trim()) {
                    return;
                }
                this.usernameTaken = data.available === false;
            } catch (err) {
                this.usernameTaken = false;
            }
        },

        async saveIdentity() {
            this.identityError = "";
            this.identitySaved = false;

            const firstName = (this.firstName || "").trim();
            const lastName = (this.lastName || "").trim();
            const username = (this.username || "").trim();

            if (!firstName || !lastName) {
                this.identityError = "First and last name are both required.";
                return;
            }
            if (!username) {
                this.identityError = "Username cannot be empty.";
                return;
            }

            // Only the three editable fields, ever. Email is deliberately
            // absent -- it travels the verification path below.
            const payload = {
                first_name: firstName,
                last_name: lastName,
                username,
            };

            this.savingIdentity = true;
            try {
                const data = await updateMyProfile(payload);
                this.profile = data.profile || this.profile;
                this.usernameTaken = false;
                this.identitySaved = true;
            } catch (err) {
                this.identityError = this.errorMessage(
                    err,
                    "We could not save your details. Please try again."
                );
                // A rejected username is the likeliest cause; re-render the
                // inline warning so the field itself shows the problem.
                if (/username/i.test(this.identityError)) {
                    this.usernameTaken = true;
                }
            } finally {
                this.savingIdentity = false;
            }
        },

        async saveEmail() {
            this.emailError = "";
            this.emailPending = "";

            const newEmail = (this.newEmail || "").trim();
            if (!newEmail) {
                this.emailError = "Enter the new email address.";
                return;
            }
            if (!this.emailPassword) {
                this.emailError =
                    "Enter your current password to change your email.";
                return;
            }

            this.savingEmail = true;
            try {
                await requestEmailChange(newEmail, this.emailPassword);
                // NOTE: the account email has NOT changed. Show a pending
                // state and keep displaying the existing address, so the
                // UI never implies a change that has not happened.
                this.emailPending = newEmail;
                this.newEmail = "";
                this.emailPassword = "";
            } catch (err) {
                this.emailError = this.errorMessage(
                    err,
                    "We could not start that email change. Please try again."
                );
            } finally {
                this.savingEmail = false;
            }
        },

        async savePassword() {
            this.passwordError = "";
            this.passwordSaved = false;

            // Client-side checks are a courtesy that saves a round trip.
            // The server independently requires and verifies the current
            // password -- these checks are not the security boundary.
            if (!this.currentPassword) {
                this.passwordError =
                    "Enter your current password to set a new one.";
                return;
            }
            if (!this.newPassword || !this.newPassword.trim()) {
                this.passwordError = "Enter a new password.";
                return;
            }
            // The shared account-password policy (utils/passwordPolicy.js,
            // twin of api/src/password_policy.py, which is the AUTHORITY).
            // Measured on the TRIMMED value because that is the string the
            // server stores, so whitespace cannot pad a short password past
            // the check on either side.
            const policy = validatePassword(this.newPassword);
            if (policy !== true) {
                this.passwordError = policy;
                return;
            }
            if (this.newPassword !== this.confirmPassword) {
                this.passwordError = "The new passwords do not match.";
                return;
            }

            this.savingPassword = true;
            try {
                await changeMyPassword(
                    this.currentPassword,
                    this.newPassword,
                    this.signOutOthers
                );
                this.passwordSaved = true;
                this.currentPassword = "";
                this.newPassword = "";
                this.confirmPassword = "";
                this.signOutOthers = false;
            } catch (err) {
                this.passwordError = this.errorMessage(
                    err,
                    "We could not change your password. Please try again."
                );
            } finally {
                this.savingPassword = false;
            }
        },
    },
};
</script>

<style scoped>
/*
 * Two columns on wide viewports, collapsing to one on narrow ones.
 * `auto-fit` + `minmax()` is intrinsically responsive: each card claims
 * a column while there is room for at least two at the floor width,
 * and drops to one column once there is not -- no viewport breakpoint
 * needed, so this behaves correctly inside all four app chromes (SA /
 * Distributor / Portal / Storefront) even though their sidebars leave
 * different amounts of room.
 *
 * The floor is bounded on BOTH sides, and both bounds were measured on
 * a deployed sandbox rather than guessed:
 *
 *   - It must stay ABOVE max-width / 3 (1100 / 3 =~ 367px, so > 350px
 *     once the two gaps are counted), or auto-fit packs THREE columns
 *     at the full max-width -- not what "two columns" asks for.
 *   - It must stay LOW enough that two columns actually appear on a
 *     common laptop. The app chrome is not free: measured on 8087, the
 *     Distributor AND SuperAdmin sidebars each consume 316px, so a
 *     1280px viewport leaves only 964px of container. At a 480px floor
 *     that is 964 < (480*2 + 24 gap = 984) -- ONE column, i.e. the
 *     feature would not appear at all on a 1280px screen. 420px needs
 *     only 864px of container, so 1280px gets two columns at ~470px
 *     each, still generous for the full-width inputs.
 *
 * 420px satisfies both: three columns would require a floor below
 * 350px, and two columns now appear from 1280px viewport upward.
 *
 * `align-items: start` stops a tall card (e.g. Security) in one
 * column from stretching its row-mate short card to match -- cards
 * keep their own height.
 */
.profile-settings {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(420px, 1fr));
    align-items: start;
    gap: 24px;
    max-width: 1100px;
}

.profile-card {
    /* min-width: 0 overrides the grid-item default of `auto`, which
       would otherwise stop a card from shrinking below its content's
       intrinsic width and defeat the single-column collapse on narrow
       viewports. */
    min-width: 0;
    border: 1px solid var(--tf-color-border, #e0e0e0);
    border-radius: var(--tf-radius-md, 8px);
    padding: 20px;
    background: var(--tf-color-surface, #fff);
}

.profile-card__title {
    font-size: 1.05rem;
    font-weight: 600;
    margin: 0 0 12px;
}

.profile-card__meta {
    font-size: 0.875rem;
    color: var(--tf-color-text-muted, #666);
    margin: 0 0 12px;
}

.profile-card__hint {
    font-size: 0.8125rem;
    color: var(--tf-color-text-muted, #666);
    margin: 0 0 16px;
}

.profile-field {
    display: block;
    margin-bottom: 14px;
}

.profile-field__label {
    display: block;
    font-size: 0.8125rem;
    font-weight: 500;
    margin-bottom: 4px;
}

.profile-field__input {
    width: 100%;
    padding: 8px 10px;
    border: 1px solid var(--tf-color-border, #ccc);
    border-radius: var(--tf-radius-sm, 4px);
    font: inherit;
    background: var(--tf-color-surface, #fff);
}

.profile-field__warning {
    font-size: 0.8125rem;
    color: var(--tf-color-warning, #b26a00);
    margin: -8px 0 12px;
}

.profile-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.875rem;
    margin-bottom: 14px;
}

.profile-settings__error {
    color: var(--tf-color-error, #c62828);
    font-size: 0.875rem;
    margin: 0 0 12px;
}

.profile-settings__success {
    color: var(--tf-color-success, #2e7d32);
    font-size: 0.875rem;
    margin: 0 0 12px;
}

.profile-settings__loading {
    font-size: 0.875rem;
    color: var(--tf-color-text-muted, #666);
}

.profile-btn {
    padding: 8px 16px;
    border: none;
    border-radius: var(--tf-radius-sm, 4px);
    background: var(--tf-color-primary, #1976d2);
    color: #fff;
    font: inherit;
    font-weight: 500;
    cursor: pointer;
}

.profile-btn:disabled {
    opacity: 0.6;
    cursor: default;
}
</style>
